import {createSlice, createAction} from '@reduxjs/toolkit';
import {USER_SLICE_NAME} from '@jobseeker/common-core/constants';
import {HYDRATE} from 'next-redux-wrapper';

import type {UserState, AppState} from '@jobseeker/common-core/types';
import {getAppliedJobCount} from '@jobseeker/common-core/reducers/job/thunkServices/jobdetail';
import {getSavedJobCount} from '@jobseeker/common-core/reducers/job/thunkServices/jobfeed';
import {
  saveUserSettings,
  getUserSettings,
} from '@jobseeker/common-core/reducers/user/thunkServices/index';
import {showErrorToast} from '../../utils';

const initialState: UserState = {
  profile: {
    id: 0,
    name: '',
    email: '',
    phone: '',
    gender: '',
    country: '',
    countryCode: 0,
    createdAt: '',
    experienceMonth: 0,
    experienceYear: 0,
    derivedExpYear: 0,
    derivedExpMonth: 0,
    passingYear: 0,
    experienceStatus: 0,
    obfuscatedUserId: '',
    resumeUpdated: true,
    proMembership: {
      isValid: false,
      daysLeft: 0,
    },
    currentCTC: '',
    currentSalary: 0,
    expectedCTC: '',
    expectedSalary: 0,
    coverLetterId: 0,
    settings: {
      emailSettings: {
        jobFeed: 0,
        boost: 0,
        jobAction: 0,
        promotions: 0,
        education: 0,
        jobApply: 0,
        followUp: 0,
        testPending: 0,
        chat: 0,
        search: 0,
        unpublish: 0,
      },
      pnSettings: {
        jobFeed: 0,
        boost: 0,
        jobAction: 0,
        promotions: 0,
        education: 0,
        chat: 0,
        search: 0,
        unpublish: 0,
      },
      accountSettings: {
        hideResume: 0,
        coverLetter: 0,
        similarCourseEnquiry: 0,
        hideStories: 0,
        invalidEmail: 0,
        twoFactorAuth: 0,
        emailChangeRequestedOn: '',
        whatsappConsent: 0,
        linkedinConsent: 0,
        callConsent: null,
      },
    },
    isShortlisted: false,
    dob: '0000-00-00',
    schema: {},
    currentSalaryUpdatedInLast30Days: false,
    verifiedEmail: 0,
    verifiedNumber: 0,
  },
  completeness: {
    percentage: 0,
    profileStatus: {
      personalDetails: false,
      professionalDetails: false,
      educationDetails: false,
      resumeAttached: false,
      followKeywords: false,
      skills: false,
      recommendations: false,
      profilePicture: false,
      profileVideoAudio: false,
      connectionsLinkedIn: false,
      connectionsFacebook: false,
      connectionsTwitter: false,
      verificationEmail: false,
      verificationPhone: false,
    },
  },
  appliedJobCount: 0,
  savedJobCount: 0,
  profileLoading: null as boolean | null,
  interviewSlots: {
    count: 0,
    slots: [{}],
  },
};

const HyDrate = createAction<AppState>(HYDRATE);

const userSlice = createSlice({
  name: USER_SLICE_NAME,
  initialState,
  reducers: {
    set_profile_loading: (state, action) => {
      state.profileLoading = action.payload as boolean;
    },
    set_user_profile: (state, action) => {
      const {settings} = action.payload;
      state.profile = {
        ...state.profile,
        ...action.payload,
        settings: {
          emailSettings: {
            ...state.profile.settings.emailSettings,
            ...settings?.emailSettings,
          },
          pnSettings: {
            ...state.profile.settings.pnSettings,
            ...settings?.pnSettings,
          },
          accountSettings: {
            ...state.profile.settings.accountSettings,
            ...settings?.accountSettings,
          },
        },
      };
    },
    clear_user_profile: (state, action) => {
      state.profile = initialState.profile;
    },
    set_profile_completeness: (state, action) => {
      state.completeness = action.payload;
    },
    set_profile_setting_coverletter: (state, action) => {
      state.profile.settings.accountSettings.coverLetter = action.payload;
    },
    set_user_interviewslots: (state, action) => {
      state.interviewSlots = action.payload;
    },
    set_roundzero: (state, action) => {
      state.profile.videoPath = action.payload.videoPath;
      if (state.profile.video) {
        state.profile.video.path = action.payload.videoPath;
      }
      state.profile.audioPath = action.payload.audioPath;
      if (state.profile.audio) {
        state.profile.audio.path = action.payload.audioPath;
      }
    },
    set_user_resume_updated: (state, action) => {
      state.profile.resumeUpdated = action.payload;
    },
    set_profile_schema: (state, action) => {
      state.profile.schema = action.payload;
    },
    clear_profile_completeness: (state, action) => {
      state.completeness = initialState.completeness;
    },
    adjust_saved_jobs_count: (state, action) => {
      if (action.payload === 'unsave') {
        state.savedJobCount = Math.max((state.savedJobCount ?? 0) - 1, 0);
      } else {
        state.savedJobCount = (state.savedJobCount ?? 0) + 1;
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(getAppliedJobCount.fulfilled, (state, action) => {
      return {
        ...state,
        appliedJobCount: action.payload.count,
      };
    });
    builder.addCase(getAppliedJobCount.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });
    builder.addCase(getSavedJobCount.fulfilled, (state, action) => {
      return {
        ...state,
        savedJobCount: action.payload.count,
      };
    });
    builder.addCase(getSavedJobCount.rejected, (state, action) => {
      console.error(action.payload, 'rejected.payload');
    });

    builder.addCase(saveUserSettings.fulfilled, (state, action) => {
      state.profile.settings = {
        emailSettings: {
          ...state.profile.settings.emailSettings,
          ...action.payload?.emailSettings,
        },
        pnSettings: {
          ...state.profile.settings.pnSettings,
          ...action.payload?.pnSettings,
        },
        accountSettings: {
          ...state.profile.settings.accountSettings,
          ...action.payload?.accountSettings,
        },
      };
    });
    builder.addCase(saveUserSettings.rejected, (state, action) => {
      showErrorToast('Failed to update, try again', action.payload);
    });

    builder.addCase(getUserSettings.fulfilled, (state, action) => {
      return {
        ...state,
        profile: {...state.profile, settings: action.payload},
      };
    });
    builder.addCase(getUserSettings.rejected, (state, action) => {
      // enqueueSnackbar('Failed to get settings, reload the page', {
      //   variant: 'error',
      // });
    });

    builder.addCase(HyDrate, (state, action) => {
      if (!action.payload?.app) {
        return state;
      }

      state.profile = action.payload?.user?.profile;
      if (action.payload?.user?.completeness) {
        state.completeness = action.payload.user.completeness;
      }
    });
  },
});

const reducer = userSlice.reducer;
export {reducer, initialState};
